/**
 * Route react-toastify calls to the on-screen status bar (in-layout banners).
 * Mutates the shared `toast` object so existing `import { toast } from 'react-toastify'`
 * call sites pick this up without a mass rewrite.
 *
 * Install from index.js AFTER installToastOutageGuard.
 */
import { toast } from 'react-toastify';
import { isApiOutage } from '../services/apiAvailability';
import {
  pushStatusMessage,
  updateStatusMessage,
  dismissStatusMessage,
} from './statusMessageBus';

let installed = false;

function mapType(method) {
  if (method === 'warn' || method === 'warning') return 'warning';
  if (method === 'loading') return 'loading';
  return method;
}

function tryOnScreen(method, content, options = {}) {
  return pushStatusMessage(mapType(method), content, options);
}

/**
 * Install once from app bootstrap (index.js), after installToastOutageGuard.
 */
export function installOnScreenNotify() {
  if (installed || typeof toast?.info !== 'function') return;
  installed = true;

  const original = {
    info: toast.info.bind(toast),
    success: toast.success.bind(toast),
    error: toast.error.bind(toast),
    warn: toast.warn?.bind(toast),
    warning: toast.warning?.bind(toast),
    loading: toast.loading?.bind(toast),
    update: toast.update.bind(toast),
    dismiss: toast.dismiss.bind(toast),
  };

  const wrap = (method) => (content, options) => {
    if (method === 'error' && isApiOutage()) {
      return undefined;
    }
    const id = tryOnScreen(method, content, options || {});
    if (id != null) return id;
    return original[method]?.(content, options);
  };

  toast.info = wrap('info');
  toast.success = wrap('success');
  toast.error = wrap('error');
  if (original.warn) toast.warn = wrap('warn');
  if (original.warning) toast.warning = wrap('warning');
  if (original.loading) {
    toast.loading = (content, options) => {
      const id = tryOnScreen('loading', content, { autoClose: false, ...(options || {}) });
      if (id != null) return id;
      return original.loading(content, options);
    };
  }

  toast.update = (id, opts = {}) => {
    const nextType = opts.type
      || (opts.isLoading ? 'loading' : undefined);
    const mapped = nextType === 'default' ? 'info' : nextType;
    updateStatusMessage(id, {
      type: mapped,
      render: opts.render,
      autoClose: opts.autoClose,
      isLoading: opts.isLoading,
    });
    try {
      original.update(id, opts);
    } catch {
      /* toast id may not exist if we never used corner toast */
    }
    return id;
  };

  toast.dismiss = (id) => {
    dismissStatusMessage(id);
    try {
      original.dismiss(id);
    } catch {
      /* ignore */
    }
  };
}

export default installOnScreenNotify;
